import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { fileExtension } from './fileKinds';

// Text past this is cut: a multi-megabyte log in one <pre> freezes the tab.
const MAX_TEXT_CHARS = 1_000_000;

function useObjectUrl(blob) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!blob) return undefined;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

function useBlobText(blob) {
  const [state, setState] = useState({ text: null, truncated: false });
  useEffect(() => {
    let cancelled = false;
    setState({ text: null, truncated: false });
    blob.text().then((raw) => {
      if (cancelled) return;
      setState({ text: raw.slice(0, MAX_TEXT_CHARS), truncated: raw.length > MAX_TEXT_CHARS });
    }).catch(() => { if (!cancelled) setState({ text: '', truncated: false }); });
    return () => { cancelled = true; };
  }, [blob]);
  return state;
}

export function ImageViewer({ blob, file }) {
  const url = useObjectUrl(blob);
  const [actualSize, setActualSize] = useState(false);
  const [broken, setBroken] = useState(false);
  if (!url) return null;
  if (broken) return <div className="atlas-doc-viewer-message">This image can't be displayed. Download it to open it.</div>;
  return (
    <div className={`atlas-image-viewer${actualSize ? ' is-actual' : ''}`}>
      {/* SVGs load as <img>, so any script inside them never runs. */}
      <button type="button" className="atlas-image-viewer-btn" onClick={() => setActualSize((v) => !v)} title={actualSize ? 'Fit to panel' : 'Actual size'}>
        <img src={url} alt={file?.filename || 'Image'} onError={() => setBroken(true)} />
      </button>
    </div>
  );
}

export function TextViewer({ blob, file, kind }) {
  const { text, truncated } = useBlobText(blob);
  if (text == null) return <div className="atlas-doc-viewer-message">Loading…</div>;

  let body = text;
  const ext = fileExtension(file?.filename);
  if (ext === 'json' || ext === 'geojson' || String(file?.mime_type || '').includes('json')) {
    try { body = JSON.stringify(JSON.parse(text), null, 2); } catch { /* show as-is */ }
  }

  return (
    <div className="atlas-text-viewer">
      {truncated ? <div className="atlas-doc-banner atlas-doc-banner-muted">Showing the first 1,000,000 characters. Download the file for the rest.</div> : null}
      {kind === 'markdown' ? (
        <div className="atlas-text-viewer-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>
      ) : (
        <pre className="atlas-text-viewer-code">{body}</pre>
      )}
    </div>
  );
}

export function HtmlViewer({ blob }) {
  const { text } = useBlobText(blob);
  if (text == null) return <div className="atlas-doc-viewer-message">Loading…</div>;
  // sandbox="" : no scripts, no forms, no same-origin access to the app.
  return <iframe className="atlas-html-viewer" title="HTML preview" sandbox="" srcDoc={text} />;
}

export function MediaViewer({ blob, kind }) {
  const url = useObjectUrl(blob);
  if (!url) return null;
  return (
    <div className="atlas-media-viewer">
      {kind === 'video' ? <video src={url} controls /> : <audio src={url} controls />}
    </div>
  );
}
